/**
 * Audio Session API (W3C draft, shipped in Safari 16.4+): tells iOS how the
 * page uses audio. Without it Safari picks a category itself; after the
 * microphone has been used it can stay in play-and-record, which routes
 * output to the phone's earpiece (receiver) instead of the speaker.
 * "playback" = music app: loudspeaker/headphones, plays with the silent switch on.
 * No-op where unsupported (Chromium, Firefox).
 */
export type AudioSessionType = "auto" | "playback" | "transient" | "transient-solo" | "ambient" | "play-and-record";

interface AudioSessionLike { type: AudioSessionType; }

function session(g: typeof globalThis): AudioSessionLike | undefined {
  return (g.navigator as (Navigator & { audioSession?: AudioSessionLike }) | undefined)?.audioSession;
}

export function hasAudioSession(g: typeof globalThis = globalThis): boolean {
  return !!session(g);
}

/** Returns true when the type was applied. */
export function setAudioSessionType(type: AudioSessionType, g: typeof globalThis = globalThis): boolean {
  const s = session(g);
  if (!s) return false;
  try {
    if (s.type !== type) s.type = type;
    return true;
  } catch {
    return false; // some engines throw while a capture is active; keep the current routing
  }
}
